import clsx from 'clsx'
import type { InfrastructurePayload, ReviewEditTarget, VmSizeCheck } from '@/types/infrastructure'
import { summariseInfrastructure } from '@/utils/infraPayload'
import { Button } from '@/components/common/Button'
import page from '@/pages/NewDeploymentPage.module.css'
import styles from './Infrastructure.module.css'

interface InfrastructureReviewProps {
  payload: InfrastructurePayload | null
  /** The VM size each new server is built with, when the product is sized from the catalog: shown beside the server. */
  vmSizes?: readonly VmSizeCheck[]
  /** Goes back to the step that holds a section's choices. */
  onEdit: (step: ReviewEditTarget) => void
}

/** Every infrastructure choice, grouped, each section with an Edit button that goes back to where it was chosen. */
export function InfrastructureReview({ payload, vmSizes, onEdit }: InfrastructureReviewProps) {
  if (!payload) {
    return <p className={styles.note}>The infrastructure details are not complete yet. Go back to the Infrastructure &amp; Settings step to finish them.</p>
  }
  return (
    <div className={styles.reviewSection} data-testid="infrastructure-review">
      {summariseInfrastructure(payload, vmSizes).map((section) => (
        <section key={section.title} aria-label={section.title} className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h5 className={styles.readOnlyTitle}>{section.title}</h5>
            {section.editStep && (
              <Button type="button" variant="ghost" size="sm" aria-label={`Edit ${section.title}`} onClick={() => onEdit(section.editStep as ReviewEditTarget)}>
                Edit
              </Button>
            )}
          </div>
          <div className={page.reviewTable}>
            {section.rows.map((row) => (
              <div key={row.label} className={page.reviewRow}>
                <span className={page.reviewLabel}>{row.label}</span>
                <span className={clsx(page.reviewValue, row.mono && page.reviewValueMono)}>{row.value}</span>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
